import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

/**
 * Custom hook for responsive design utilities
 * Provides device detection and responsive value helpers
 */
const useResponsive = () => {
  const theme = useTheme();
  
  // Device breakpoint detections
  const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // < 600px
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md')); // 600px - 900px
  const isDesktop = useMediaQuery(theme.breakpoints.up('md')); // >= 900px
  const isLargeDesktop = useMediaQuery(theme.breakpoints.up('lg')); // >= 1200px
  const isXLDesktop = useMediaQuery(theme.breakpoints.up('xl')); // >= 1536px
  
  // Specific device checks
  const isMobileOnly = useMediaQuery(theme.breakpoints.only('xs')); // 0-600px
  const isTabletOnly = useMediaQuery(theme.breakpoints.only('sm')); // 600-900px
  const isSmallDesktop = useMediaQuery(theme.breakpoints.only('md')); // 900-1200px
  
  // Orientation detection
  const isPortrait = useMediaQuery('(orientation: portrait)');
  const isLandscape = useMediaQuery('(orientation: landscape)');
  
  // Touch device detection
  const isTouchDevice = useMediaQuery('(hover: none) and (pointer: coarse)');
  
  /**
   * Get responsive value based on current breakpoint
   * @param {*} mobile - Value for mobile devices (< 600px)
   * @param {*} tablet - Value for tablet devices (600-900px)
   * @param {*} desktop - Value for desktop devices (>= 900px)
   * @param {*} largeDesktop - Optional value for large desktops (>= 1200px)
   * @returns {*} Appropriate value for current device
   */
  const getResponsiveValue = (mobile, tablet, desktop, largeDesktop = null) => {
    if (largeDesktop && isLargeDesktop) return largeDesktop;
    if (isDesktop) return desktop;
    if (isTablet) return tablet;
    return mobile;
  };
  
  /**
   * Get responsive spacing value (for padding, margin, gap)
   * @param {number} mobile - Spacing for mobile (multiplied by 8px)
   * @param {number} tablet - Spacing for tablet (multiplied by 8px)
   * @param {number} desktop - Spacing for desktop (multiplied by 8px)
   * @returns {number} Appropriate spacing value
   */
  const getResponsiveSpacing = (mobile, tablet, desktop) => {
    return getResponsiveValue(mobile, tablet, desktop);
  };
  
  /**
   * Get responsive typography variant
   * @param {string} mobile - Typography variant for mobile
   * @param {string} tablet - Typography variant for tablet
   * @param {string} desktop - Typography variant for desktop
   * @returns {string} Appropriate typography variant
   */
  const getResponsiveTypography = (mobile, tablet, desktop) => {
    return getResponsiveValue(mobile, tablet, desktop);
  };
  
  /**
   * Get responsive grid columns
   * @param {number} mobile - Columns for mobile
   * @param {number} tablet - Columns for tablet
   * @param {number} desktop - Columns for desktop
   * @returns {number} Appropriate number of columns
   */
  const getResponsiveGrid = (mobile, tablet, desktop) => {
    return getResponsiveValue(mobile, tablet, desktop);
  };
  
  /**
   * Get responsive container props for MUI Container
   * @param {string} maxWidth - Max width ('sm', 'md', 'lg', 'xl')
   * @returns {object} Container props
   */
  const getContainerProps = (maxWidth = 'lg') => {
    return {
      maxWidth: maxWidth,
      sx: {
        px: getResponsiveSpacing(2, 3, 4), // Responsive horizontal padding
        py: getResponsiveSpacing(2, 3, 4), // Responsive vertical padding
      }
    };
  };
  
  /**
   * Get responsive content props with bottom navigation spacing
   * @param {boolean} showBottomNav - Whether bottom nav is visible
   * @returns {object} Content props
   */
  const getContentProps = (showBottomNav = false) => {
    return {
      sx: {
        pb: showBottomNav && (isMobile || isTablet) ? 8 : 0, // Add padding for bottom nav
        minHeight: '100vh',
      }
    };
  };
  
  /**
   * Get responsive font size
   * @param {number} base - Base font size in rem
   * @returns {string} Responsive font size
   */
  const getResponsiveFontSize = (base) => {
    const multiplier = getResponsiveValue(0.875, 1, 1.125);
    return `${base * multiplier}rem`;
  };
  
  /**
   * Get responsive button size
   * @returns {string} MUI button size
   */
  const getResponsiveButtonSize = () => {
    return getResponsiveValue('medium', 'medium', 'large');
  };
  
  /**
   * Get responsive icon size
   * @returns {string} Icon size
   */
  const getResponsiveIconSize = () => {
    return getResponsiveValue('medium', 'medium', 'large');
  };
  
  /**
   * Check if current viewport is small (mobile or small tablet)
   * @returns {boolean}
   */
  const isSmallScreen = () => {
    return isMobile || (isTablet && isPortrait);
  };
  
  /**
   * Get responsive dialog props
   * @returns {object} Dialog props
   */
  const getDialogProps = () => {
    return {
      fullScreen: isMobile,
      maxWidth: getResponsiveValue('xs', 'sm', 'md'),
      fullWidth: true,
    };
  };
  
  return {
    // Device detections
    isMobile,
    isTablet,
    isDesktop,
    isLargeDesktop,
    isXLDesktop,
    isMobileOnly,
    isTabletOnly,
    isSmallDesktop,
    
    // Orientation
    isPortrait,
    isLandscape,
    
    // Device type
    isTouchDevice,
    
    // Helper functions
    getResponsiveValue,
    getResponsiveSpacing,
    getResponsiveTypography,
    getResponsiveGrid,
    getContainerProps,
    getContentProps,
    getResponsiveFontSize,
    getResponsiveButtonSize,
    getResponsiveIconSize,
    isSmallScreen,
    getDialogProps,
  };
};

export default useResponsive;
